import { HubConnectionBuilder, LogLevel, HubConnectionState, HttpTransportType } from '@aspnet/signalr';
import get from 'lodash/get';
import { EventBus } from '@/helpers/event-bus';
import { NotificationType } from '@/models/enums';
import { logger } from '@/helpers/log.service';
import { getLocale } from '@/helpers';
import { userModulesHelper } from '@/helpers/modules-helper';

export default {
  async install(Vue, { store, actions }) {
    await store.getters[actions.SETT_GET_STG_WAITER];

    const appSett = store.getters[actions.SETT_GET_APPSET];
    const appSettTransport = get(appSett, 'signalRTransport');
    let transportKey;
    let transportValue;
    if (appSettTransport) {
      const tRegexp = new RegExp(appSettTransport, 'i');
      transportKey = Object.keys(HttpTransportType).find(k => tRegexp.test(k));
      transportValue = HttpTransportType[`${transportKey}`];
    }
    logger.debug(`[signalR plugin] install. build connection. 
    appSettTransport=${appSettTransport}. transportKey=${transportKey}. transportValue = ${transportValue}`);

    const cfg = await store.getters[actions.SETT_GET_CFG_WAITER];
    logger.debug('[signalR plugin] install. config:', cfg);

    const connection = new HubConnectionBuilder()
      .withUrl(`${cfg.apiHost}/hubs/default-hub`, {
        accessTokenFactory: () => store.getters[actions.AUTH_GET_ACCESTOKENGET],
        transport: transportValue
      })
      .configureLogging(LogLevel.Information)
      .build();

    const isUserLoggined = () => store.getters[actions.AUTH_GET_ISLOGINEDGET];
    const connectionStateChanged = (isConnected) => {
      store.commit(actions.SETTTEMP_MUT_SIGNALRCONNECTED, isConnected);
      EventBus.$emit('signalr-connection-state-changed');
    };
    connection.onclose(() => {
      connectionStateChanged(false);
      if (isUserLoggined()) { start(); }
    });

    let defaultRetryConnectTimeout = 6000;
    let retryCount = 0;
    let startedPromise: Promise<void | {}> | undefined;
    function start() {
      retryCount++;
      startedPromise = connection
        .start()
        .then(() => {
          retryCount = 0;
          connectionStateChanged(true);
        })
        .catch(err => {
          if (err.statusCode === 401) {
            logger.debug(`Failed to connect with hub. Reason: Unauthorized.`);
            retryCount = 0;
            return new Promise((resolve) => resolve(true));
          }
          let multiplier = Math.floor(retryCount / 10) || 1;
          if (!isUserLoggined() || retryCount > 100) {
            logger.debug(`Failed to connect with hub. Retry count = ${retryCount - 1}.`);
            retryCount = 0;
            return new Promise((resolve) => resolve(true));
          } else {
            let retryTimeout = multiplier * defaultRetryConnectTimeout;
            logger.debug(`Failed to connect with hub. Retry count = ${retryCount}. Will retry in ${retryTimeout / 1000} sec`);
            return new Promise((resolve, reject) => setTimeout(() => start().then(() => resolve(true)).catch(reject), retryTimeout));
          }
        });
      return startedPromise;
    }

    if (store.getters[actions.AUTH_GET_ISLOGINEDGET]) {
      start();
    }

    EventBus.$on('user-logged', () => {
      if (connection.state !== HubConnectionState.Connected) {
        start();
      }
    });

    EventBus.$on('token-refreshed', () => {
      if (connection.state !== HubConnectionState.Connected && retryCount === 0) {
        start();
      }
    });

    EventBus.$on('user-logged-out', () => {
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop();
      }
    });

    connection.on('SendUserGotDocument', data => {
      logger.debug('SignalR SendUserGotDocument', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', { documentVersionCode: data.documentVersionCode, notificationType: NotificationType.UserGotDocument });
    });

    connection.on('SendAuthorGotDecision', data => {
      logger.debug('SignalR SendAuthorGotDecision', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', { documentVersionCode: data.documentVersionCode, notificationType: NotificationType.AuthorGotDecision });
    });

    connection.on('SendDocumentGotNewComment', data => {
      logger.debug('SignalR SendDocumentGotNewComment', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', { documentVersionCode: data.documentVersionCode, notificationType: NotificationType.DocumentGotNewComment });
      EventBus.$emit('document-got-new-comment', data);
    });

    connection.on('SendBalanceChanged', data => {
      logger.debug('SignalR SendBalanceChanged', data);
      store.dispatch(actions.DOC_ACT_LOADBALANCE);
    });

    connection.on('SendUserGotInvitation', data => {
      logger.debug('SignalR SendUserGotInvitation', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', { documentVersionCode: '', notificationType: NotificationType.UserGotInvitation });
    });

    connection.on('SendInvitationAuthorGotDecision', data => {
      logger.debug('SignalR SendInvitationAuthorGotDecision', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', { documentVersionCode: '', notificationType: NotificationType.InvitationAuthorGotDecision });
    });

    connection.on('SendUserDeletedFromOrganization', data => {
      logger.debug('SignalR SendUserDeletedFromOrganization', data);
      store.commit(actions.ORG_MUT_MYORGCLEAR, { silent: true });
      Promise.all([store.dispatch(actions.AUTH_ACT_REFRESHTOKEM), store.dispatch(actions.DOC_ACT_LOADBALANCE), store.dispatch(actions.DOC_ACT_LOADCOUNTERS)])
        .then(() => EventBus.$emit('rebuild-navigation'))
        .finally(() => EventBus.$emit('document-notification', { documentVersionCode: '', notificationType: NotificationType.UserDeletedFromOrganization }));
    });

    connection.on('SendUserGotDiiaSignResult', data => {
      logger.debug('[SignalR:SendUserGotDiiaSignResult]', data);
      EventBus.$emit('app-sign-diia-user-got-result', data);
    });

    connection.on('SendUserModuleActivated', data => {
      logger.debug('[SignalR:SendUserModuleActivated]', data);
      EventBus.$emit(
        'document-notification',
        {
          message: getLocale('signalRNotifications.moduleActivated', { moduleName: data.moduleName }),
          notificationType: NotificationType.ModuleActivated
        });
      if (!userModulesHelper.isModuleActivated(data.moduleCode)) {
        userModulesHelper.refreshActivatedModules();
      }
      EventBus.$emit('user-modules-changed');
    });

    connection.on('SendUserModulePaymentFailed', data => {
      logger.debug('[SignalR:SendUserModulePaymentFailed]', data);
      EventBus.$emit('user-modules-changed');
    });

    connection.on('SendUserGotReferralBonus', data => {
      logger.debug('SignalR SendUserGotReferralBonus', data);
      EventBus.$emit(
        'document-notification',
        {
          message: getLocale('signalRNotifications.userGotReferralBonus'),
          notificationType: NotificationType.UserGotReferralBonus
        });
      store.dispatch(actions.DOC_ACT_LOADBALANCE);
    });

    connection.on('SendUserCancelledDocumentSign', data => {
      logger.debug('SignalR SendUserCancelledDocumentSign', data);
      store.dispatch(actions.DOC_ACT_LOADCOUNTERS);
      EventBus.$emit('document-notification', {
        documentVersionCode: data.documentVersionCode,
        notificationType: NotificationType.UserCancelledDocumentSign
      });
    });
  }
};
